// feature-flag.service.ts
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, of, retry, timeout } from 'rxjs';

/** `true`/`false` for an app-wide flag, or a list of office ids for a flag that is on per office. */
type FeatureFlags = { [key: string]: boolean | string[] };

/** Last payload that loaded cleanly, so a failed fetch degrades to the previous set, not to none. */
const FLAG_CACHE_KEY = 'feature-flags-cache';

/** Ceiling per attempt. Two attempts worst case, so bootstrap is gated for ~8s at the very most. */
const FLAG_LOAD_TIMEOUT_MS = 4000;

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private flags: FeatureFlags = {};

  constructor(private http: HttpClient) {}

  /**
   * Resolves come what may — never rejects, never hangs.
   *
   * This runs as an `APP_INITIALIZER`, so Angular will not create the root component until the
   * returned promise settles. The previous version handed back the raw `HttpClient` promise with
   * no timeout and no catch: one stalled or failed request for `assets/feature-flags.json` left
   * the app on the `index.html` loading screen indefinitely, which is the reported "never loads
   * until you press refresh". `assets/**` is `installMode: lazy` in ngsw-config.json, so on a cold
   * cache this genuinely goes to the network and is exactly what a flaky connection drops.
   *
   * A blocked bootstrap is far worse than a stale flag set, so on failure we fall back to the last
   * good payload and let the app start.
   */
  loadFlags(): Promise<void> {
    return firstValueFrom(
      this.http.get<FeatureFlags>('assets/feature-flags.json').pipe(
        timeout(FLAG_LOAD_TIMEOUT_MS),
        retry({ count: 1, delay: 300 }),
        catchError(() => of(null))
      )
    )
      .then(flags => {
        if (flags) {
          this.flags = flags;
          this.cacheFlags(flags);
          return;
        }
        console.error('Feature flags failed to load; falling back to the last cached set.');
        this.flags = this.readCachedFlags();
      })
      .catch(() => {
        // Belt and braces. Nothing above should throw, and bootstrap must proceed if it does.
        this.flags = this.readCachedFlags();
      });
  }

  isFeatureEnabled(feature: string): boolean {
    return this.flags[feature] === true;
  }

  /**
   * On when the flag is `true` (every office) or lists this office's id. A missing flag or an
   * empty list is off, so a per-office flag ships switched off until offices are added to it.
   */
  isFeatureEnabledForOffice(feature: string, officeId: string): boolean {
    const value = this.flags[feature];
    if (value === true) {
      return true;
    }
    return Array.isArray(value) && !!officeId && value.some(id => String(id) === String(officeId));
  }

  private cacheFlags(flags: FeatureFlags): void {
    try {
      localStorage.setItem(FLAG_CACHE_KEY, JSON.stringify(flags));
    } catch {
      // Private mode / quota — the cache is an optimisation, not a requirement.
    }
  }

  /** `{}` on a first visit, which turns every flag off. Degraded, but the app starts. */
  private readCachedFlags(): FeatureFlags {
    try {
      const cached = localStorage.getItem(FLAG_CACHE_KEY);
      const parsed = cached ? JSON.parse(cached) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
}
